/*
  Infinite scroll for the collection product grid.

  Rendered by main-collection-product-grid.liquid when the section's
  "Product loading style" setting is "Infinite scroll". The <infinite-scroll>
  element sits between the product grid and the pagination and carries the
  next page URL (including any active filter/sort/search params) in
  data-next-url.

  It is a custom element on purpose: facets.js replaces the whole
  #ProductGridContainer innerHTML whenever a filter or sort changes, which
  reconnects a fresh element carrying the new filter-aware next URL, and
  disconnects the old one (tearing down its observer and in-flight request).
  The pagination stays in the markup as the no-JS fallback and is hidden here.
*/

if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.loading = false;
        this.abortController = null;
        this.spinner = this.querySelector('.loading__spinner');
        this.retryButton = this.querySelector('.infinite-scroll__retry');

        this.retryButton?.addEventListener('click', () => this.loadNextPage());

        this.pagination = this.parentElement?.querySelector('.pagination-wrapper');
        this.pagination?.classList.add('hidden');

        // Start fetching one viewport-height before the sentinel is reached.
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
          },
          { rootMargin: '0px 0px 100% 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.abortController?.abort();
      }

      finish() {
        this.observer?.disconnect();
        this.spinner?.classList.add('hidden');
        this.retryButton?.classList.add('hidden');
      }

      isNearViewport() {
        
        return this.getBoundingClientRect().top <= window.innerHeight * 2;
      }

      async loadNextPage() {
        if (this.loading) return;
        if (!this.dataset.nextUrl) {
          this.finish();
          return;
        }

        this.loading = true;
        this.retryButton?.classList.add('hidden');
        this.spinner?.classList.remove('hidden');

        this.abortController?.abort();
        this.abortController = new AbortController();

         
        const url = new URL(this.dataset.nextUrl, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);

        try {
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Infinite scroll: request failed (${response.status})`);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

          this.appendItems(doc);

          const nextSentinel = doc.querySelector('infinite-scroll');
          if (nextSentinel?.dataset.nextUrl) {
            this.dataset.nextUrl = nextSentinel.dataset.nextUrl;
            this.spinner?.classList.add('hidden');
             
            requestAnimationFrame(() => {
              if (this.dataset.nextUrl && !this.loading && this.isNearViewport()) this.loadNextPage();
            });
          } else {
            delete this.dataset.nextUrl;
            this.finish();
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
            this.spinner?.classList.add('hidden');
            this.retryButton?.classList.remove('hidden');
          }
        } finally {
          this.loading = false;
        }
      }

      appendItems(doc) {
        const grid = document.getElementById('product-grid');
        if (!grid) return;

        const productPath = (item) => {
          const link = item.querySelector('a[href*="/products/"]');
          return link ? link.href.split('?')[0] : null;
        };

        const existing = new Set();
        grid.querySelectorAll(':scope > *').forEach((item) => {
          const path = productPath(item);
          if (path) existing.add(path);
        });

        const appended = [];
        doc.querySelectorAll('#product-grid > *').forEach((item) => {
          const path = productPath(item);
          if (path && existing.has(path)) return;
          if (path) existing.add(path);
          grid.appendChild(item);
          appended.push(item);
        });

        
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(grid);
        }
        document.dispatchEvent(
          new CustomEvent('collection:products-appended', {
            detail: { grid, items: appended },
          })
        );
      }
    }
  );
}
